import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { AnimatePresence } from "framer-motion";
//
import MainTemplate from "@/components/Templates/MainTemplate";
import HeroSection from "./HeroSection";
import WebinarListing from "./WebinarList";
import WatchPastWebinars from "./WatchPastWebinars";
import BottomForm from "./BottomForm";
import FloatingBanner from "./FloatingBanner";

const RegisterModal = dynamic(() => import("./RegisterModal"), { ssr: false });
//
import { handleScrollToSection } from "../../utils";
import { useWindowDimensions } from "../../hooks";
import useDisableIntercom from "../../hooks/useDisableIntercom";
import { getWebinarListData } from "@/redux/WebinarReducer";
//
import { mapRegisterModalData, mapWebinarData } from "./mapWebinarListPageData";
//
function WebinarListPage() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { width } = useWindowDimensions();

  const urlParams = router.query || "";
  let searchString = "";
  for (const key in urlParams) {
    searchString = `${searchString}${key}=${urlParams[key]}&`;
  }

  useDisableIntercom({});
  const [showModal, setShowModal] = useState(false);
  const [isRegisteredUser, setIsRegisteredUser] = useState(false);
  const [registerModalData, setRegisterModalData] = useState(false);
  const [userData, setUserData] = useState(null);

  const removeFixBtn = useRef(null);
  const upcomingWebinarRef = useRef(null);
  const pastWebinarRef = useRef(null);
  const fixedRef = useRef(null);

  const slug = "webinars";
  const isWebinarRecordingsPage = slug === "webinar-recordings"; // TODO: Remove this check as Recordings page move out of this folder

  const getWebinarList = () =>
    dispatch(getWebinarListData({ slug: `v2/${slug}` }));

  useEffect(() => {
    getWebinarList();
  }, []);

  const { isLoading, success, error, data } = useAppSelector(
    (state) => state.webinar
  );

  const {
    heroSectionData,
    upcomingWebinarData,
    pastWebinarData,
    bottomFormData,
  } = useMemo(() => mapWebinarData(data), [data]);

  useEffect(() => {
    const detectScroll = (stopPos, btn) => {
      window.onscroll = () => {
        let footerPos =
          stopPos?.getBoundingClientRect()?.top - window.innerHeight;
        if (footerPos >= 0) {
          btn.style.opacity = "1";
          btn.style.visibility = "visible";
        } else {
          btn.style.opacity = "0";
          btn.style.visibility = "hidden";
        }
      };
    };
    let stopPos = removeFixBtn.current;
    let fixRef = fixedRef.current;
    if (fixRef) {
      detectScroll(stopPos, fixRef);
    }
  });

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const _userData = localStorage.getItem("userData");
    if (userId) {
      setIsRegisteredUser(true);
      if (_userData) setUserData(JSON.parse(_userData));
    }
  }, []);

  const handleScrollToBottomForm = () => {
    let cardDiv = removeFixBtn.current;
    cardDiv.scrollIntoView({
      behavior: "smooth",
      block: width <= 1023 ? "start" : "center",
    });
  };

  const onShowModalClick = (modalData) => {
    setRegisterModalData({
      ...modalData,
      ...mapRegisterModalData(modalData, data),
    });
    setShowModal(true);
  };

  const handleScrollToUpcomingWebinar = () =>
    handleScrollToSection({ refElement: upcomingWebinarRef });

  const handleScrollToPastWebinar = () =>
    handleScrollToSection({ refElement: pastWebinarRef });

  return (
    <MainTemplate
      // isLoading={isLoading}
      error={error}
      success={success}
      hideLinks
      isStickyHeader={false}
    >
      <HeroSection
        width={width}
        handleScrollToUpcomingWebinar={handleScrollToUpcomingWebinar}
        handleScrollToPastWebinar={handleScrollToPastWebinar}
        isWebinarRecordingsPage={isWebinarRecordingsPage}
        showWebinarList={
          !isWebinarRecordingsPage && upcomingWebinarData?.showWebinarList
        }
        showPastWebinarList={pastWebinarData?.showPastWebinarList}
        data={heroSectionData}
      />
      {!isWebinarRecordingsPage && upcomingWebinarData?.showWebinarList && (
        <WebinarListing
          upcomingWebinarRef={upcomingWebinarRef}
          data={data}
          width={width}
          onShowModalClick={onShowModalClick}
        />
      )}
      {pastWebinarData?.showPastWebinarList && (
        <WatchPastWebinars
          pastWebinarRef={pastWebinarRef}
          data={pastWebinarData}
          width={width}
          onShowModalClick={onShowModalClick}
          isRegisteredUser={isRegisteredUser}
          urlParams={searchString}
          handleScrollToPastWebinar={handleScrollToPastWebinar}
          isWebinarRecordingsPage={isWebinarRecordingsPage}
        />
      )}
      <BottomForm
        data={bottomFormData}
        urlParams={searchString}
        slug={slug}
        fixedButtonRef={removeFixBtn}
        isWebinarRecordingsPage={isWebinarRecordingsPage}
      />
      <FloatingBanner
        data={data}
        fixedRef={fixedRef}
        handleScrollToSection={handleScrollToBottomForm}
        width={width}
        userData={isWebinarRecordingsPage && userData}
      />
      <AnimatePresence>
        {showModal && (
          <RegisterModal
            urlParams={searchString}
            slug={slug}
            width={width}
            setShowModal={setShowModal}
            setIsRegisteredUser={setIsRegisteredUser}
            setUserData={setUserData}
            isWebinarRecordingsPage={isWebinarRecordingsPage}
            {...registerModalData}
          />
        )}
      </AnimatePresence>
    </MainTemplate>
  );
}

WebinarListPage.propTypes = {
  isLoading: PropTypes.bool,
  error: PropTypes.instanceOf(Object),
  success: PropTypes.bool,
};

WebinarListPage.defaultProps = {
  isLoading: false,
  error: {},
  success: true,
};

export default WebinarListPage;
